/**
 * Logging an hour on a task by hand, from the task's own page.
 *
 * The clock in the top bar covers the case where somebody is at the keyboard
 * while the work happens. This covers the rest of them, which is most of them: a
 * phone call that ran long, an afternoon at a customer's office, and the hour
 * yesterday that nobody started a timer for. Perfex had both on the task screen
 * and the operators who still opened it were opening it for one of the two.
 *
 * WHY NOT `TimeEntryForm`, which already logs time. That one is the project
 * tab's toolbar: a row of controls whose centre is a task PICKER, scoped to one
 * project. On a task page the answer to "which task" is the page, so the picker
 * is a control with one option, and a toolbar in the middle of a card column is
 * the wrong shape besides. What the two share is the conversion of hours to
 * minutes and of a day to an instant, and those now live in `projects/lib.ts`
 * where all three time dialogs read them.
 *
 * THE TASK IS FIXED AND THE PROJECT IS SENT WITH IT. `time.routes.ts` will
 * derive the project from the task on its own, but the entry is posted with
 * both for the same reason the project form does it: these hours end up on an
 * invoice at some project's rate, and a wrong answer there is silent and lands
 * in money. A task with no project posts none, which is a state the schema
 * allows and the route handles.
 *
 * AN EMPTY DATE MEANS NOW, and that is not laziness in the field. The route
 * stamps `new Date()` when `startedAt` is absent, which keeps the clock time,
 * and with it the order that entries logged on the same day come back in. The
 * field is prefilled with today, so a day only goes on the wire once the
 * operator has actually moved it - see the submit.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { DatePicker } from "@wa/ui";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { createTimeEntry } from "../../../lib/projects";
import { Btn, CheckField, Modal, useToast } from "../../ui";
import { UserPicker } from "../_shared/UserPicker";
import { fmtHours, toDateInput, toIso } from "../projects/lib";
import { useCrmUser } from "../../shell/CrmUser";

export function TaskTimeModal({
  taskId,
  projectId,
  onClose,
  onSaved,
}: {
  taskId: string;
  /** The task's project, or null for a task filed under none. */
  projectId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const toast = useToast();
  const user = useCrmUser();

  // Today, in the operator's own day rather than UTC's - the same rule the edit
  // dialog's prefill follows, and the reason `toDateInput` exists.
  const today = toDateInput(new Date().toISOString());
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(today);
  // Defaults to the person filling the form in, which is right nine times out of
  // ten. It is a field rather than an assumption because the tenth time is real:
  // logging a colleague's afternoon is part of keeping a timesheet, and until
  // this could be said the record claimed the operator had done the work.
  const [userId, setUserId] = useState<string | null>(user.id);
  const [billable, setBillable] = useState(true);
  const [busy, setBusy] = useState(false);

  const parsed = parseFloat(hours);
  const valid = Number.isFinite(parsed) && parsed > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    const minutes = Math.max(1, Math.round(parsed * 60));
    try {
      await createTimeEntry({
        taskId,
        ...(projectId ? { projectId } : {}),
        minutes,
        note: note.trim() || undefined,
        billable,
        // The empty string is what "nobody" is stored as, and it has to be sent
        // EXPLICITLY: the route falls back to the signed-in user on an absent
        // field, so dropping the key would hand the hours to the operator after
        // they had just taken their own name off them.
        userId: userId ?? "",
        // Only once the day has been moved off today. Sending today's midnight
        // instead of nothing would flatten the order of everything logged this
        // morning, since the list is sorted by this column.
        ...(date && date !== today ? { startedAt: toIso(date) } : {}),
      });
      // `fmtHours`, not a raw number: the timer's own stop toast says "2,5 h"
      // and this one saying "2.5 h" would be the same sentence written two ways
      // on two screens a minute apart.
      toast(t("timer.recorded", { hours: fmtHours(minutes) }));
      onSaved();
      onClose();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const title = t("projects.time.addTitle");

  return (
    <Modal title={title} label={title} onClose={onClose} className="max-w-[460px]">
      <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="crm-lab">{t("projects.hours")} *</span>
            <label className="crm-inp">
              <input
                type="number"
                step="0.25"
                min="0"
                inputMode="decimal"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="0.5"
                aria-label={t("projects.hours")}
                autoFocus
                required
              />
            </label>
          </div>
          <div>
            <span className="crm-lab">{t("projects.time.date")}</span>
            <DatePicker value={date} onChange={setDate} locale={locale} placeholder={t("projects.time.date")} />
          </div>
        </div>

        {/* Says "nobody" in a sentence when the picker is empty, the same way the
            edit dialog does. A search box with no chip under it reads as a
            control that failed to load rather than as an answer. */}
        <div>
          <span className="crm-lab">{t("projects.loggedBy")}</span>
          <UserPicker value={userId} onChange={setUserId} />
          {userId ? null : <p className="crm-mut mt-1.5 text-[12.5px]">{t("projects.unassigned")}</p>}
        </div>

        <div>
          <span className="crm-lab">{t("projects.note")}</span>
          <label className="crm-inp">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("projects.notePlaceholder")}
              autoComplete="off"
            />
          </label>
        </div>

        <CheckField checked={billable} onChange={setBillable} label={t("projects.billable")} />

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !valid}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
